/**
 * weekly-events-loader.js
 * Fetches Tilted Gaming's Google Calendar ICS, parses it (with full RRULE /
 * EXDATE / RECURRENCE-ID support), then renders the current week's Tue–Sun
 * schedule into the #tg-weekly-grid element.
 *
 * Ported from the mobile app's lib/events/calendarApi.ts.
 */
(function () {
  'use strict';

  // ── Config ─────────────────────────────────────────────────────────────────

  var CALENDAR_ID =
    'b2529d0528779531b649f027c52cd63f5df21fb1196c82e952021849e8edc276' +
    '%40group.calendar.google.com';

  var DIRECT_ICS =
    'https://calendar.google.com/calendar/ical/' + CALENDAR_ID + '/public/basic.ics';

  // LocalStorage cache — avoid re-fetching on every page load
  var CACHE_KEY = 'tg_ics_v2';
  var CACHE_TTL = 30 * 60 * 1000; // 30 minutes

  function getCached() {
    try {
      var item = localStorage.getItem(CACHE_KEY);
      if (!item) return null;
      var data = JSON.parse(item);
      if (Date.now() - data.ts > CACHE_TTL) return null;
      return data.text;
    } catch (e) { return null; }
  }

  function setCache(text) {
    try {
      localStorage.setItem(CACHE_KEY, JSON.stringify({ text: text, ts: Date.now() }));
    } catch (e) { /* ignore quota errors */ }
  }

  // CORS proxies tried in order until one succeeds
  var PROXIES = [
    // api.codetabs.com removed 2026-09-03. It returns HTTP 522 with NO
    // Access-Control-Allow-Origin, so it can never succeed from a browser — and it
    // was FIRST in this list, so every page load wasted a request and logged a CORS
    // error before falling through to the worker below. Do not re-add it.
    // Measured the same day: the worker is the ONLY working entry here (200, full
    // feed, ACAO *). api.allorigins.win also returns 522 and corsproxy.io returns
    // 403, so the two below the worker are dead too — left in place only because
    // the loop never reaches them while the worker answers. Treat this chain as
    // having no real fallback.
    'https://tg-ics-proxy.royal-dream-93db.workers.dev',
    // Supabase ical-proxy, added 2026-09-03 as a REAL fallback. The three public
    // proxies that used to sit below the worker are all dead (codetabs 522,
    // allorigins 522, corsproxy.io 403), so until now the worker had no working
    // backup and the whole site's calendar hung on it.
    // This one is self-hosted, already in production for the TV leaderboard, needs
    // no auth (verify_jwt is off), and returns 200 with Access-Control-Allow-Origin
    // '*' plus the full feed. Verified it serves the SAME calendar before wiring it
    // in: identical 120 UIDs and identical sorted SUMMARY list against a direct
    // fetch. The VEVENT order differs, which is fine — every consumer here parses
    // the whole feed rather than relying on order.
    // The URL takes no query parameter: the calendar is fixed inside the function,
    // so DIRECT_ICS is deliberately not appended.
    'https://emqkrszhbqydkujminhi.supabase.co/functions/v1/ical-proxy',
    'https://api.allorigins.win/raw?url=' + encodeURIComponent(DIRECT_ICS),
    'https://corsproxy.io/?' + encodeURIComponent(DIRECT_ICS),
  ];

  var FETCH_TIMEOUT_MS = 10000; // 10s per proxy

  function fetchWithTimeout(url) {
    var ctrl = typeof AbortController !== 'undefined' ? new AbortController() : null;
    var timer = ctrl ? setTimeout(function () { ctrl.abort(); }, FETCH_TIMEOUT_MS) : null;
    var opts = ctrl ? { signal: ctrl.signal } : {};
    var p = fetch(url, opts);
    if (timer) p = p.finally ? p.finally(function () { clearTimeout(timer); }) : p;
    return p;
  }

  var DAYS = ['Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
  var DAY_SHORT = ['TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'];

  // ── Timezone helpers ───────────────────────────────────────────────────────

  function isLAPDT(d) {
    var m = d.getMonth();
    if (m < 2 || m > 10) return false;
    if (m > 2 && m < 10) return true;
    var yr = d.getFullYear(), day = d.getDate();
    if (m === 2) {
      var firstDow = new Date(yr, 2, 1).getDay();
      var secondSun = 8 + ((7 - firstDow) % 7);
      return day >= secondSun;
    }
    var firstDow2 = new Date(yr, 10, 1).getDay();
    var firstSun = 1 + ((7 - firstDow2) % 7);
    return day < firstSun;
  }

  function parseIcalDate(value, tzid) {
    if (!value.includes('T')) {
      return new Date(
        parseInt(value.slice(0, 4), 10),
        parseInt(value.slice(4, 6), 10) - 1,
        parseInt(value.slice(6, 8), 10),
        0, 0, 0
      );
    }
    var y  = parseInt(value.slice(0, 4), 10);
    var mo = parseInt(value.slice(4, 6), 10) - 1;
    var d  = parseInt(value.slice(6, 8), 10);
    var h  = parseInt(value.slice(9, 11), 10);
    var mi = parseInt(value.slice(11, 13), 10);
    var s  = parseInt(value.slice(13, 15), 10);
    if (value.endsWith('Z')) return new Date(Date.UTC(y, mo, d, h, mi, s));
    if (tzid && (tzid.includes('Los_Angeles') || tzid.includes('Pacific'))) {
      var offset = isLAPDT(new Date(y, mo, d)) ? 7 : 8;
      return new Date(Date.UTC(y, mo, d, h + offset, mi, s));
    }
    return new Date(y, mo, d, h, mi, s);
  }

  // ── Description parsing ────────────────────────────────────────────────────

  function stripHtml(html) {
    return html
      .replace(/<br\s*\/?>/gi, '\n').replace(/<\/li>/gi, '\n')
      .replace(/<[^>]+>/g, '').replace(/&amp;/g, '&').replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>').replace(/&nbsp;/g, ' ').replace(/\r/g, '')
      .replace(/\n{3,}/g, '\n\n').trim();
  }

  function parseDescription(raw) {
    if (!raw) return {};
    var text = stripHtml(raw);
    var checkinTime, entryFee, maxParticipants;
    text.split('\n').forEach(function (line) {
      var t = line.trim();
      var ci = t.match(/^check\s*[- ]?in:\s*(.+)/i);
      if (ci) { checkinTime = ci[1].trim(); return; }
      var en = t.match(/^entry:\s*(.+)/i);
      if (en) { entryFee = en[1].trim(); return; }
      var mp1 = t.match(/^(\d+)\s+max\s+participants/i);
      if (mp1) { maxParticipants = mp1[1]; return; }
      var mp2 = t.match(/^max\s+participants?:\s*(\d+)/i);
      if (mp2) { maxParticipants = mp2[1]; }
    });
    return { checkinTime: checkinTime, entryFee: entryFee, maxParticipants: maxParticipants };
  }

  // ── RRULE expansion ────────────────────────────────────────────────────────

  var DAY_MAP = { SU: 0, MO: 1, TU: 2, WE: 3, TH: 4, FR: 5, SA: 6 };

  function expandRRule(raw, expandUntil) {
    var start    = raw.start;
    var end      = raw.end;
    var duration = end.getTime() - start.getTime();
    var exdateSet = {};
    raw.exdates.forEach(function (d) { exdateSet[d.toDateString()] = true; });

    var params = {};
    raw.rrule.split(';').forEach(function (part) {
      var eq = part.indexOf('=');
      if (eq !== -1) params[part.slice(0, eq)] = part.slice(eq + 1);
    });

    var freq     = params['FREQ'] || '';
    var interval = Math.max(1, parseInt(params['INTERVAL'] || '1', 10));
    var maxCount = params['COUNT'] ? parseInt(params['COUNT'], 10) : 500;
    var ruleUntil = expandUntil;
    if (params['UNTIL']) {
      try {
        var u = parseIcalDate(params['UNTIL']);
        if (u < ruleUntil) ruleUntil = u;
      } catch (e) { /* ignore */ }
    }

    var byDays = null;
    if (params['BYDAY']) {
      byDays = params['BYDAY'].split(',')
        .map(function (s) { return DAY_MAP[s.replace(/^[+\-]?\d+/, '')]; })
        .filter(function (n) { return n !== undefined; })
        .sort(function (a, b) { return a - b; });
    }

    var desc = parseDescription(raw.description);

    function makeEvent(occStart) {
      return {
        id: raw.uid + '-' + occStart.getTime(),
        title: raw.title,
        start: new Date(occStart),
        end: new Date(occStart.getTime() + duration),
        checkinTime: desc.checkinTime,
        entryFee: desc.entryFee,
        maxParticipants: desc.maxParticipants
      };
    }

    var result = [], count = 0;

    if (freq === 'WEEKLY') {
      var startDow  = start.getDay();
      var targetDows = (byDays && byDays.length > 0) ? byDays : [startDow];

      var refSun = new Date(start);
      refSun.setDate(start.getDate() - startDow);
      var weekIdx = 0;

      outer: while (weekIdx <= 260) {
        var weekSun = new Date(refSun);
        weekSun.setDate(refSun.getDate() + weekIdx * 7 * interval);
        if (weekSun > ruleUntil) break;

        for (var di = 0; di < targetDows.length; di++) {
          var dow = targetDows[di];
          var occ = new Date(weekSun);
          occ.setDate(weekSun.getDate() + dow);
          occ.setHours(start.getHours(), start.getMinutes(), start.getSeconds(), 0);
          if (occ < start) continue;
          if (occ > ruleUntil) break outer;
          if (exdateSet[occ.toDateString()]) continue;
          result.push(makeEvent(occ));
          count++;
          if (count >= maxCount) break outer;
        }
        weekIdx++;
      }
    }
    // (DAILY/MONTHLY/YEARLY omitted — TG only uses WEEKLY recurring)

    return result;
  }

  // ── iCal parser ────────────────────────────────────────────────────────────

  function parseIcal(text) {
    var unfolded = text.replace(/\r\n[ \t]/g, '').replace(/\n[ \t]/g, '');
    var lines = unfolded.split(/\r?\n/);
    var rawEvents = [], inEvent = false;
    var cur = null;

    lines.forEach(function (rawLine) {
      var line = rawLine.trimEnd !== undefined ? rawLine.trimEnd() : rawLine.replace(/\s+$/, '');
      if (line === 'BEGIN:VEVENT') {
        inEvent = true;
        cur = { exdates: [], allDay: false };
        return;
      }
      if (line === 'END:VEVENT') {
        if (cur && cur.uid && cur.title && cur.start && cur.end) rawEvents.push(cur);
        inEvent = false;
        return;
      }
      if (!inEvent || !cur) return;

      var colon = line.indexOf(':');
      if (colon === -1) return;
      var key     = line.slice(0, colon);
      var val     = line.slice(colon + 1);
      var baseKey = key.split(';')[0];
      var tzidM   = key.match(/TZID=([^;:]+)/);
      var tzid    = tzidM ? tzidM[1] : undefined;

      switch (baseKey) {
        case 'UID':         cur.uid   = val; break;
        case 'SUMMARY':
          cur.title = val.replace(/\\n/g, ' ').replace(/\\,/g, ',').replace(/\\;/g, ';').trim();
          break;
        case 'DESCRIPTION':
          cur.description = val.replace(/\\n/g, '\n').replace(/\\,/g, ',').trim() || undefined;
          break;
        case 'DTSTART':
          cur.start = parseIcalDate(val, tzid);
          cur.allDay = !val.includes('T');
          break;
        case 'DTEND':   cur.end  = parseIcalDate(val, tzid); break;
        case 'RRULE':   cur.rrule = val; break;
        case 'EXDATE':
          val.split(',').forEach(function (v) {
            try { cur.exdates.push(parseIcalDate(v.trim(), tzid)); } catch (e) { /* skip */ }
          });
          break;
        case 'RECURRENCE-ID':
          cur.recurrenceId = parseIcalDate(val, tzid);
          break;
      }
    });

    var expandUntil = new Date();
    expandUntil.setDate(expandUntil.getDate() + 60);

    var byUid = {};
    rawEvents.forEach(function (ev) {
      if (!byUid[ev.uid]) byUid[ev.uid] = { bases: [], overrides: [] };
      if (ev.recurrenceId) byUid[ev.uid].overrides.push(ev);
      else                 byUid[ev.uid].bases.push(ev);
    });

    var allEvents = [];
    Object.keys(byUid).forEach(function (uid) {
      var group = byUid[uid];

      var overrideMap = {};
      group.overrides.forEach(function (ov) {
        if (!ov.title || !ov.start || !ov.end || !ov.recurrenceId) return;
        var desc = parseDescription(ov.description);
        overrideMap[ov.recurrenceId.toDateString()] = {
          id: ov.uid + '-override-' + ov.start.getTime(),
          title: ov.title, start: ov.start, end: ov.end,
          checkinTime: desc.checkinTime, entryFee: desc.entryFee,
          maxParticipants: desc.maxParticipants
        };
      });

      group.bases.forEach(function (base) {
        if (!base.title || !base.start || !base.end) return;
        if (base.rrule) {
          expandRRule(base, expandUntil).forEach(function (inst) {
            allEvents.push(overrideMap[inst.start.toDateString()] || inst);
          });
        } else {
          var desc = parseDescription(base.description);
          allEvents.push({
            id: base.uid, title: base.title, start: base.start, end: base.end,
            checkinTime: desc.checkinTime, entryFee: desc.entryFee,
            maxParticipants: desc.maxParticipants
          });
        }
      });
    });

    return allEvents.sort(function (a, b) { return a.start - b.start; });
  }

  // ── Week window ────────────────────────────────────────────────────────────

  function getWeekDates() {
    // Returns an array of 6 Dates: [Tue, Wed, Thu, Fri, Sat, Sun]
    var today = new Date();
    today.setHours(0, 0, 0, 0);
    var dow = today.getDay(); // 0=Sun…6=Sat

    // Find this week's Tuesday
    var tuesday = new Date(today);
    if (dow === 0) {
      // Sunday – show the week that just ended (last Tue)
      tuesday.setDate(today.getDate() - 5);
    } else if (dow === 1) {
      // Monday (closed) – show the coming week
      tuesday.setDate(today.getDate() + 1);
    } else {
      // Tue(2)–Sat(6): backtrack to this week's Tuesday
      tuesday.setDate(today.getDate() - (dow - 2));
    }

    var dates = [];
    for (var i = 0; i < 6; i++) {
      var d = new Date(tuesday);
      d.setDate(tuesday.getDate() + i);
      dates.push(d);
    }
    return dates;
  }

  function fmtTime(date) {
    var h = date.getHours(), m = date.getMinutes();
    var ampm = h >= 12 ? 'PM' : 'AM';
    h = h % 12 || 12;
    return h + (m > 0 ? ':' + String(m).padStart(2, '0') : '') + ' ' + ampm;
  }

  /* Check-in times are FREE TEXT, typed by staff into the calendar description
     ("6:30pm", "6:00pm", "4pm"), while start times come from fmtTime above
     ("7 PM", "12:30 PM"). So the two never agreed on a card, and the check-ins
     did not even agree with each other. Reformat through the same function.

     This REPLACES the time in place rather than returning a formatted time,
     so any surrounding words a staff member wrote are preserved, and anything
     it cannot parse is returned untouched. It fails OPEN on purpose: a time we
     cannot read is still information, and dropping it would be a worse bug
     than an inconsistent format. */
  function fmtCheckin(raw) {
    if (!raw) return raw;
    return String(raw).replace(
      /(\d{1,2})(?::(\d{2}))?\s*([ap])\.?m\.?/i,
      function (whole, hh, mm, ap) {
        var h = parseInt(hh, 10);
        var mi = mm ? parseInt(mm, 10) : 0;
        if (h < 1 || h > 12 || mi > 59) return whole;
        var d = new Date(2000, 0, 1, (h % 12) + (ap.toLowerCase() === 'p' ? 12 : 0), mi);
        return fmtTime(d);
      }
    );
  }

  function fmtDate(date) {
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  }

  // ── Game accent colors ─────────────────────────────────────────────────────

  /* These are GAME_ACCENT from the app (lib/constants/games.ts) — the ELEVENTH
     copy of the per-game colour list in this project, and before 2026-09-04
     every value here disagreed with it. Flesh and Blood was red-orange where
     the app, the leaderboards, the TV board and Top Players all make it green;
     Riftbound was teal against purple; Pokemon yellow against red. The same
     game changed colour between this section and Top Players one scroll below.
     Four games were missing entirely and fell through to the default, so Grand
     Archive, Union Arena, Azuki and Cookie Run all rendered brand purple.
     Titles here are free text from the calendar, so the MATCHING stays by
     keyword and only the colours are shared. Add a game here when one is added
     to the app — nothing checks this automatically. */
  function gameAccent(title) {
    var t = title.toLowerCase();
    if (/flesh|fab|armory|sage|compendium/.test(t))  return '#22c55e';
    if (/magic|mtg|fnm|draft|lorwyn|commander/.test(t)) return '#c084fc';
    if (/one.?piece|\bop\b|\bop-\d/.test(t))           return '#f97316';
    if (/gundam/.test(t))                            return '#3b82f6';
    if (/riftbound|nexus/.test(t))                   return '#a855f7';
    if (/grand.?archive|\bga\b/.test(t))                return '#f59e0b';
    if (/lorcana/.test(t))                           return '#38bdf8';
    if (/union.?arena/.test(t))                      return '#fb7185';
    if (/pok[eé]mon/.test(t))                        return '#ef4444';
    if (/azuki/.test(t))                             return '#ec4899';
    if (/cookie.?run/.test(t))                       return '#14b8a6';
    return '#8c3cff';
  }

  // ── Render ─────────────────────────────────────────────────────────────────

  function render(events) {
    var grid        = document.getElementById('tg-weekly-grid');
    var rangeLabel  = document.getElementById('tg-week-range');
    if (!grid) return;

    var weekDates = getWeekDates();
    var today = new Date(); today.setHours(0,0,0,0);

    // Date range label
    if (rangeLabel) {
      rangeLabel.textContent =
        fmtDate(weekDates[0]) + ' \u2013 ' + fmtDate(weekDates[5]);
    }

    // Filter events to this week
    var weekStart = new Date(weekDates[0]); weekStart.setHours(0,0,0,0);
    var weekEnd   = new Date(weekDates[5]); weekEnd.setHours(23,59,59,999);
    var weekEvents = events.filter(function (e) {
      /* includes("closed") caught CLOSED FOR HOLIDAY and Store is Closed! but
         NOT "Closing Early!" or "EASTER Closing @ 4pm", so three of the six
         closure notices in the feed rendered as ordinary event cards — with an
         accent colour and a start time, as if the store were running something.
         Identical regex to isClosureEvent() in the app
         (lib/events/calendarApi.ts), where this exact bug was fixed already;
         the word boundaries keep it off any title that merely contains the
         letters. */
      return e.start >= weekStart && e.start <= weekEnd &&
             !/\bclos(ed|ing)\b/i.test(e.title);
    });

    grid.innerHTML = '';

    weekDates.forEach(function (date, i) {
      var isToday = date.toDateString() === today.toDateString();
      /* A day that has already happened looked exactly like one still to come.
         Only isToday existed, so on a Thursday the Tuesday and Wednesday
         columns read as live events, and on a Sunday five of the six columns
         were history with nothing saying so. The date is still printed, so this
         is reinforcement rather than the only signal. */
      var isPast = date < today;

      // Events for this day
      var dayEvents = weekEvents.filter(function (e) {
        return e.start.toDateString() === date.toDateString();
      });

      // Column
      var col = document.createElement('div');
      col.className = 'tge-day' + (isToday ? ' tge-today' : '') + (isPast ? ' tge-past' : '');

      // Header
      var hdr = document.createElement('div');
      hdr.className = 'tge-day-hdr';
      hdr.innerHTML =
        '<span class="tge-day-name">' + DAY_SHORT[i] + '</span>' +
        '<span class="tge-day-date">' + fmtDate(date) + '</span>' +
        (isToday ? '<span class="tge-today-badge">TODAY</span>' : '');
      col.appendChild(hdr);

      // Events
      var evList = document.createElement('div');
      evList.className = 'tge-events';

      if (dayEvents.length === 0) {
        var empty = document.createElement('div');
        empty.className = 'tge-empty';
        empty.textContent = 'No events';
        evList.appendChild(empty);
      } else {
        dayEvents.forEach(function (ev) {
          var accent = gameAccent(ev.title);
          var card = document.createElement('div');
          card.className = 'tge-event';
          card.style.setProperty('--accent', accent);

          var name = document.createElement('div');
          name.className = 'tge-event-name';
          name.textContent = ev.title;
          card.appendChild(name);

          if (ev.checkinTime) {
            var ci = document.createElement('div');
            ci.className = 'tge-event-checkin';
            ci.innerHTML = '<span class="tge-label">Check-in</span> ' + fmtCheckin(ev.checkinTime);
            card.appendChild(ci);
          }

          var startTime = document.createElement('div');
          startTime.className = 'tge-event-start';
          startTime.innerHTML =
            '<span class="tge-label">Start</span> ' + fmtTime(ev.start);
          card.appendChild(startTime);

          var meta = document.createElement('div');
          meta.className = 'tge-event-meta';

          if (ev.maxParticipants) {
            var pl = document.createElement('span');
            pl.className = 'tge-meta-item tge-players';
            pl.innerHTML = '<svg width="11" height="11" viewBox="0 0 24 24" fill="currentColor"><path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z"/></svg>' + ev.maxParticipants;
            meta.appendChild(pl);
          }

          if (ev.entryFee) {
            var fee = document.createElement('span');
            fee.className = 'tge-meta-item tge-fee';
            fee.textContent = ev.entryFee;
            meta.appendChild(fee);
          }

          if (meta.children.length > 0) card.appendChild(meta);
          evList.appendChild(card);
        });
      }

      col.appendChild(evList);
      grid.appendChild(col);
    });
  }

  function showError() {
    var grid = document.getElementById('tg-weekly-grid');
    if (!grid) return;
    grid.innerHTML =
      '<div class="tge-load-msg">Unable to load schedule. <a href="/pages/events">View full calendar →</a></div>';
  }

  function showLoading() {
    var grid = document.getElementById('tg-weekly-grid');
    if (!grid) return;
    var html = '';
    for (var i = 0; i < 6; i++) {
      html += '<div class="tge-day tge-skeleton">' +
        '<div class="tge-day-hdr"><span class="tge-skel-bar" style="width:2.5rem"></span><span class="tge-skel-bar" style="width:3.5rem"></span></div>' +
        '<div class="tge-events">' +
          '<div class="tge-skel-event"></div>' +
          (i % 2 === 0 ? '<div class="tge-skel-event"></div>' : '') +
        '</div></div>';
    }
    grid.innerHTML = html;
  }

  // ── Init ───────────────────────────────────────────────────────────────────

  function tryFetch(urls, index) {
    if (index >= urls.length) return Promise.reject(new Error('All proxies failed'));
    return fetchWithTimeout(urls[index])
      .then(function (res) {
        if (!res.ok) throw new Error('HTTP ' + res.status);
        return res.text();
      })
      .then(function (text) {
        if (!text.includes('BEGIN:VCALENDAR')) throw new Error('Not ICS');
        return text;
      })
      .catch(function (err) {
        console.warn('TG calendar proxy[' + index + '] failed:', err.message);
        return tryFetch(urls, index + 1);
      });
  }

  function init() {
    // Serve from cache instantly if available
    var cached = getCached();
    if (cached) {
      try {
        var events = parseIcal(cached);
        render(events);
        return;
      } catch (e) { /* cache corrupt, fall through to fetch */ }
    }

    showLoading();
    tryFetch(PROXIES, 0)
      .then(function (text) {
        setCache(text);
        var events = parseIcal(text);
        render(events);
      })
      .catch(function (err) {
        console.error('TG weekly events: all proxies failed', err);
        showError();
      });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', init);

})();
